import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { Store } from '@ngrx/store';
import { first, take } from 'rxjs';

import { SnackbarService } from '@services/snackbar.service';
import { ColumnOptionsService } from '@services/column-options.service';
import { DiscrepancyService } from '@services/api/discrepancy/discrepancy.service';
import { WorkflowService } from '@services/api/workflow/workflow.service';
import { UserService } from '@services/api/user/user.service';
import { UserDataService } from '@services/data/user-data.service';
import { DiscrepancyDataService } from '@services/data/discrepancy-data.service';
import { ToggleComponent } from '@shared/toggle/toggle.component';
import {
    SelectComponent,
    SelectionChangeEvent,
} from '@shared/select/select.component';
import { LoaderComponent } from '@shared/loader/loader.component';
import { TextButtonComponent } from '@shared/button/text-button/text-button.component';
import { FlatButtonComponent } from '@shared/button/flat-button/flat-button.component';
import {
    ExcelExportDialogComponent,
    ExcelExportDialogData,
} from '@main/dialogs/excel-export-dialog/excel-export-dialog.component';
import {
    TakeConfirmDialogComponent,
    TakeConfirmDialogData,
} from '@main/bulk-actions/take-confirm-dialog.component';
import {
    AssignConfirmDialogComponent,
    AssignConfirmDialogData,
} from '@main/bulk-actions/assign-confirm-dialog.component';
import {
    UnassignConfirmDialogComponent,
    UnassignConfirmDialogData,
} from '@main/bulk-actions/unassign-confirm-dialog.component';
import { TableViewMenuComponent } from '@features/table-view-menu/table-view-menu.component';
import { DiscrepencyViewComponent } from '@main/inbox/discrepancy-view/discrepancy-view.component';
import { DocumentViewComponent } from '@main/inbox/document-view/document-view.component';
import { StatusFilterComponent } from '@main/status-filter/status-filter.component';
import { SummaryPanelComponent } from '@main/summary-panel/summary-panel.component';
import { isNil } from '@helpers/index';
import { customColumnsToColumnOptions } from '@helpers/custom-column';
import { SubscriptionListType, SubscriptionList } from '@helpers/subscription';
import { Workflow } from '@models/workflow/workflow';
import { CustomColumn } from '@models/workflow/custom-column';
import { ColumnOption, SelectOption } from '@models/shared.model';
import { BulkAssignDiscrepancyRequest } from '@models/discrepancy/bulk-request';
import { StatusChipItem } from '@models/discrepancy/status-chip';
import { Role, UserInboxChoices } from '@models/user';
import { TableView } from '@constants/table';
import { selectSelectedDocument } from 'app/store/selectors/document.selectors';
import { saveSummaryPanelData } from 'app/store/actions/document.actions';
import DATA_QA from '@automation/data-qa.json';
import {
    DiscrepancyColumn,
    DiscrepancyViewColumnOptions,
    DocumentColumn,
    DocumentNestedColumn,
    DocumentViewColumnOptions,
    DocumentViewNestedColumnOptions,
} from '@main/utils';

@Component({
    selector: 'app-main-inbox',
    templateUrl: './main-inbox.component.html',
    styleUrl: 'main-inbox.component.scss',
    imports: [
        MatDividerModule,
        TableViewMenuComponent,
        TextButtonComponent,
        DiscrepencyViewComponent,
        DocumentViewComponent,
        SelectComponent,
        StatusFilterComponent,
        ToggleComponent,
        LoaderComponent,
        SummaryPanelComponent,
        FlatButtonComponent,
    ],
})
export class MainInboxComponent implements OnInit, OnDestroy {
    @ViewChild(DiscrepencyViewComponent)
    discrepancyViewComponent: DiscrepencyViewComponent | undefined;
    @ViewChild(DocumentViewComponent)
    documentViewComponent: DocumentViewComponent | undefined;

    readonly TableView: typeof TableView = TableView;
    readonly Role = Role;
    readonly dataQa = DATA_QA;
    readonly staticDocOptions = DocumentViewColumnOptions;
    readonly staticNestedDocOptions = DocumentViewNestedColumnOptions;
    readonly staticDiscOptions = DiscrepancyViewColumnOptions;
    customColumnOptions: ColumnOption[] = [];
    private customColumns: CustomColumn[] = [];

    get docOptions() {
        // Custom columns do NOT appear in the main document table,
        // only in the nested discrepancy rows.
        return [...this.staticDocOptions];
    }
    get nestedDocOptions() {
        return [...this.staticNestedDocOptions, ...this.customColumnOptions];
    }
    get discOptions() {
        return [...this.staticDiscOptions, ...this.customColumnOptions];
    }

    loading = false;
    bulkLoading = false;
    selectedTableView: TableView = TableView.Document;
    workflowInstanceOptions: SelectOption[] = [];
    repositoryOptions: SelectOption[] = [];
    statusChipsData: StatusChipItem[] = [];
    selectedWorkflow: number | undefined;
    selectedStates: number[] = [];
    showMyItems: boolean = false;
    showDocuments = false;
    selectedDocument: number | null = null;
    inboxChoicesLoading: boolean = true;
    docViewColumnOptions: ColumnOption<DocumentColumn | string>[] = [];
    docViewNestedColumnOptions: ColumnOption<DocumentNestedColumn | string>[] =
        [];
    discViewColumnOptions: ColumnOption<DiscrepancyColumn | string>[] = [];

    private inboxChoices: UserInboxChoices | undefined;
    private _workflowAndRepo: Workflow | undefined;
    private _subscriptions = new SubscriptionList() as SubscriptionListType;

    private readonly store = inject(Store);
    private readonly dialog = inject(MatDialog);
    private readonly userService = inject(UserService);
    private readonly workflowService = inject(WorkflowService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly discrepancyService = inject(DiscrepancyService);
    private readonly userDataService = inject(UserDataService);
    private readonly discrepancyDataService = inject(DiscrepancyDataService);
    private readonly columnOptionsService = inject(ColumnOptionsService);

    get workflowAndRepo(): Workflow | undefined {
        return this._workflowAndRepo;
    }

    set workflowAndRepo(value: Workflow | undefined) {
        this._workflowAndRepo = value;
        this.showDocuments = !isNil(value);
        this.fetchCustomColumns();

        if (this.showDocuments) {
            this.getStatusChips();
            return;
        }

        this.statusChipsData = [];
    }

    get selectedViewComponent() {
        return this.documentViewComponent || this.discrepancyViewComponent;
    }

    get canDoBulkAction() {
        return this.selectedViewComponent
            ? this.selectedViewComponent.hasSelectedRow
            : false;
    }

    get showBulkActionButtons() {
        return this.userDataService.currentUser?.role !== Role.Analyst;
    }

    get showWorkflowInstanceSelector() {
        return this.workflowInstanceOptions.length > 0;
    }

    get workflowSelectionRequired() {
        return (
            this.showWorkflowInstanceSelector && isNil(this.selectedWorkflow)
        );
    }

    ngOnInit() {
        this.setColumnOptions();
        this.getSummaryPanelData();
        this.setInboxChoices();
        this.getInboxChoices();
        this.listenRefreshTableTrigger();
    }

    ngOnDestroy() {
        this._subscriptions.unsubscribeAllSafe();
    }

    private listenRefreshTableTrigger() {
        this._subscriptions['table-refresh-trigger'] =
            this.discrepancyDataService.tableRefreshTrigger$.subscribe({
                next: () => this.refreshTable(false),
            });
    }

    renotify() {
        this.bulkLoading = true;
        const table =
            this.discrepancyViewComponent || this.documentViewComponent;
        const discCount = this.selectedViewComponent?.selectedDiscrepancyCount;

        if (table?.selectedBulkData) {
            const bulkRequest = (table as any).buildBulkRequest
                ? (table as any).buildBulkRequest()
                : undefined;
            if (!bulkRequest) {
                this.bulkLoading = false;
                return;
            }
            this.discrepancyService
                .bulkRenotify(bulkRequest)
                .pipe(take(1))
                .subscribe({
                    next: (res) => {
                        if (res) {
                            this.snackbarService.progress({
                                header: `Re-Notifying for ${discCount} Discrepancies`,
                                content:
                                    'We will inform you when the process is done. Please do not close this page.',
                            });
                        } else {
                            this.snackbarService.error({
                                header: `Failed Re-Notifying for ${discCount} Discrepancies`,
                                content:
                                    'Not able to renotify selected discrepancies. You can open a ticket to get help about the issue.',
                            });
                        }
                    },
                    complete: () => (this.bulkLoading = false),
                });
        }
    }

    take() {
        this.bulkLoading = true;
        const table =
            this.discrepancyViewComponent || this.documentViewComponent;
        const discCount = this.selectedViewComponent?.selectedDiscrepancyCount;

        if (table?.selectedBulkData) {
            const bulkRequest = (table as any).buildBulkRequest
                ? (table as any).buildBulkRequest()
                : undefined;
            if (!bulkRequest) {
                this.bulkLoading = false;
                return;
            }
            this.discrepancyService
                .bulkTake(bulkRequest)
                .pipe(take(1))
                .subscribe({
                    next: (res) => {
                        if (res) {
                            this.snackbarService.progress({
                                header: `Taking ${discCount} Discrepancies`,
                                content:
                                    'We will inform you when the process is done. Please do not close this page.',
                            });
                        } else {
                            this.snackbarService.error({
                                header: `Taking ${discCount} Discrepancies Failed`,
                                content:
                                    'Not able to take selected discrepancies. You can open a ticket to get help about the issue.',
                            });
                        }
                    },
                    complete: () => (this.bulkLoading = false),
                });
        }
    }

    assign(assigneeId: number, assigneeName: string) {
        this.bulkLoading = true;
        const table =
            this.discrepancyViewComponent || this.documentViewComponent;
        const discCount = this.selectedViewComponent?.selectedDiscrepancyCount;

        if (table?.selectedBulkData) {
            const bulkRequest = (table as any).buildBulkRequest
                ? (table as any).buildBulkRequest()
                : undefined;
            if (!bulkRequest) {
                this.bulkLoading = false;
                return;
            }

            const request: BulkAssignDiscrepancyRequest = {
                ...bulkRequest,
                assigneeId,
            };

            this.discrepancyService
                .bulkAssign(request)
                .pipe(take(1))
                .subscribe({
                    next: (res) => {
                        if (res) {
                            this.snackbarService.progress({
                                header: `Assigning ${discCount} Discrepancies to ${assigneeName}`,
                                content:
                                    'We will inform you when the process is done. Please do not close this page.',
                            });
                        } else {
                            this.snackbarService.error({
                                header: `Assigning ${discCount} Discrepancies Failed`,
                                content:
                                    'Not able to assign selected discrepancies. You can open a ticket to get help about the issue.',
                            });
                        }
                    },
                    complete: () => (this.bulkLoading = false),
                });
        }
    }

    openTakeConfirmDialog() {
        if (this.selectedViewComponent) {
            const dialogRef = this.dialog.open(TakeConfirmDialogComponent, {
                data: {
                    discrepancyCount:
                        this.selectedViewComponent?.selectedDiscrepancyCount,
                } as TakeConfirmDialogData,
                maxWidth: '448px',
                minWidth: '200px',
                width: '448px',
            });

            dialogRef.afterClosed().subscribe({
                next: (result: { take: boolean }) => {
                    if (result?.take) {
                        this.take();
                    }
                },
            });
        }
    }

    openAssignConfirmDialog() {
        if (!this.showBulkActionButtons) {
            return;
        }

        const workflowId = this.workflowAndRepo?.workflowId;

        if (this.selectedViewComponent && workflowId) {
            const dialogRef = this.dialog.open(AssignConfirmDialogComponent, {
                data: {
                    discrepancyCount:
                        this.selectedViewComponent.selectedDiscrepancyCount,
                    workflowId,
                } as AssignConfirmDialogData,
                maxWidth: '448px',
                minWidth: '200px',
                width: '448px',
            });

            dialogRef.afterClosed().subscribe({
                next: (
                    result:
                        | {
                              assign: boolean;
                              assigneeId: number;
                              assigneeName: string;
                          }
                        | undefined,
                ) => {
                    if (result?.assign) {
                        this.assign(result.assigneeId, result.assigneeName);
                    }
                },
            });
        }
    }

    openUnassignConfirmDialog() {
        if (this.selectedViewComponent) {
            const dialogRef = this.dialog.open(UnassignConfirmDialogComponent, {
                data: {
                    discrepancyCount:
                        this.selectedViewComponent.selectedDiscrepancyCount,
                } as UnassignConfirmDialogData,
                maxWidth: '448px',
                minWidth: '200px',
                width: '448px',
            });

            dialogRef.afterClosed().subscribe({
                next: (result: { unassign: boolean } | undefined) => {
                    if (result?.unassign) {
                        this.unassign();
                    }
                },
            });
        }
    }

    unassign() {
        this.bulkLoading = true;
        const table =
            this.discrepancyViewComponent || this.documentViewComponent;
        const discCount = this.selectedViewComponent?.selectedDiscrepancyCount;

        if (table?.selectedBulkData) {
            const bulkRequest = (table as any).buildBulkRequest
                ? (table as any).buildBulkRequest()
                : undefined;
            if (!bulkRequest) {
                this.bulkLoading = false;
                return;
            }
            this.discrepancyService
                .bulkUnassign(bulkRequest)
                .pipe(take(1))
                .subscribe({
                    next: (res) => {
                        if (res) {
                            this.snackbarService.progress({
                                header: `Unassigning ${discCount} Discrepancies`,
                                content:
                                    'We will inform you when the process is done. Please do not close this page.',
                            });
                        } else {
                            this.snackbarService.error({
                                header: `Unassigning ${discCount} Discrepancies Failed`,
                                content:
                                    'Not able to unassign selected discrepancies. You can open a ticket to get help about the issue.',
                            });
                        }
                    },
                    complete: () => (this.bulkLoading = false),
                });
        }
    }

    onExportClicked() {
        const data: ExcelExportDialogData = {
            tableView: this.selectedTableView,
            workflowAndRepo: this.workflowAndRepo,
            mine: this.showMyItems,
            states: this.selectedStates,
            filterModel: this.selectedViewComponent?.filterModel,
            columnState: this.selectedViewComponent?.columnState,
            customColumns: this.customColumns,
        };

        this.dialog.open(ExcelExportDialogComponent, {
            data,
            maxWidth: '686px',
            minWidth: '200px',
            width: '686px',
        });
    }

    onTableViewChanged(tableView: TableView) {
        this.selectedTableView = tableView;
        this.setSavedStates();
    }

    onWorkflowInstanceChange(event: SelectionChangeEvent) {
        this.selectedWorkflow = event.value;
        this.repositoryOptions = [];
        this.closePanel();
        this.setWorkflowAndRepo();
    }

    setWorkflowAndRepo() {
        const selectedWorkflow = this.selectedWorkflow;

        if (selectedWorkflow == null) {
            this.workflowAndRepo = undefined;
            return;
        }

        // Avoid creating a new reference when the workflow hasn't changed.
        // A new reference triggers ngOnChanges → resetFilters in child views,
        // which wipes sticky filter/sort state.
        if (this._workflowAndRepo?.workflowId === selectedWorkflow) {
            this.refreshTable();
            return;
        }

        this.workflowAndRepo = new Workflow(selectedWorkflow);
        this.refreshTable();
    }

    onStatusFilterChange(filters: number[]) {
        // Only the status chips that were removed should drop their selected
        // discrepancies; adding a chip keeps the current selection intact.
        const removedStates = this.selectedStates.filter(
            (state) => !filters.includes(state),
        );
        this.selectedStates = [...filters];

        if (removedStates.length) {
            const removedStatuses = this.statusChipsData
                .filter((chip) => removedStates.includes(chip.backingField))
                .map((chip) => chip.name);
            this.selectedViewComponent?.pruneSelectionByStatuses(
                removedStatuses,
            );
        }

        this.refreshTable();
    }

    onToggleMyItems(toggle: boolean) {
        this.showMyItems = toggle;
        this.refreshTable();
    }

    resetFilters() {
        const table =
            this.discrepancyViewComponent || this.documentViewComponent;
        table?.resetFilters();
    }

    isResetFiltersDisabled(): boolean {
        const table =
            this.discrepancyViewComponent || this.documentViewComponent;

        return table ? !table.filterApplied : true;
    }

    closePanel() {
        this.store.dispatch(
            saveSummaryPanelData({
                selectedDocument: null,
                selectedDiscrepancy: null,
            }),
        );
    }

    private setColumnOptions() {
        this._subscriptions['column-options'] =
            this.columnOptionsService.columnOptions$.subscribe({
                next: (options) =>
                    this.rebuildDocViewOptions(options.hiddenColumns),
            });

        this._subscriptions['nested-column-options'] =
            this.columnOptionsService.nestedColumnOptions$.subscribe({
                next: (options) =>
                    this.rebuildNestedViewOptions(options.hiddenColumns),
            });

        this._subscriptions['disc-column-options'] =
            this.columnOptionsService.discColumnOptions$.subscribe({
                next: (options) =>
                    this.rebuildDiscViewOptions(options.hiddenColumns),
            });
    }

    /** Apply hidden state to a column option list (custom IDs are pre-normalized to lowercase). */
    private applyHiddenState<T>(
        options: ColumnOption<T>[],
        hiddenColumns: string[],
    ): ColumnOption<T>[] {
        // Match case-insensitively: the backend roundtrip lowercases stored
        // hidden ids (e.g. `documentType` -> `documenttype`) while the static
        // option ids keep their original camelCase.
        const hiddenSet = new Set(hiddenColumns.map((id) => id.toLowerCase()));
        return options.map((option) =>
            hiddenSet.has(String(option.id).toLowerCase())
                ? { ...option, checked: false }
                : option,
        );
    }

    private rebuildDocViewOptions(hiddenColumns: string[]) {
        this.docViewColumnOptions = this.applyHiddenState(
            this.docOptions,
            hiddenColumns,
        );
    }

    private rebuildNestedViewOptions(hiddenColumns: string[]) {
        this.docViewNestedColumnOptions = this.applyHiddenState(
            this.nestedDocOptions,
            hiddenColumns,
        );
    }

    private rebuildDiscViewOptions(hiddenColumns: string[]) {
        // Custom columns must be re-grouped to 'disc' so they appear in the
        // discrepancy section of the Table View menu.
        const customDiscOptions = this.customColumnOptions.map((opt) => ({
            ...opt,
            group: 'disc',
        }));
        this.discViewColumnOptions = this.applyHiddenState(
            [...this.staticDiscOptions, ...customDiscOptions],
            hiddenColumns,
        );
    }

    updateColumnDisplay(e: { checked: boolean; id: any }) {
        this.columnOptionsService.updateColumnOptions({
            checked: e.checked,
            id: e.id,
        });
    }

    updateNestedColumnDisplay(e: { checked: boolean; id: any }) {
        this.columnOptionsService.updateNestedColumnOptions({
            checked: e.checked,
            id: e.id,
        });
    }

    updateDiscColumnDisplay(e: { checked: boolean; id: any }) {
        this.columnOptionsService.updateDiscColumnOptions({
            checked: e.checked,
            id: e.id,
        });
    }

    private getSummaryPanelData() {
        const selectedDocument$ = this.store.select(selectSelectedDocument);
        this._subscriptions['selected-document'] = selectedDocument$.subscribe({
            next: (data) => (this.selectedDocument = data),
        });
    }

    private getWorkflows() {
        this.loading = true;
        this._subscriptions['get-workflows'] = this.workflowService
            .getWorkflows()
            .subscribe({
                next: (data) => {
                    this.workflowInstanceOptions = data.map((item) => ({
                        value: item.id,
                        viewValue: item.name,
                    }));
                    this.reconcileSelectedWorkflow();
                },
                complete: () => (this.loading = false),
            });
    }

    refreshTable(purge: boolean = true) {
        const table =
            this.discrepancyViewComponent || this.documentViewComponent;
        table?.refreshData(purge);
    }

    private getStatusChips() {
        this.statusChipsData = [];
        this._subscriptions.unsubscribeSafe('status-chips');

        this._subscriptions['status-chips'] = this.discrepancyService
            .getDiscrepanciesStatusChips(this._workflowAndRepo)
            .pipe(take(1))
            .subscribe((data) => (this.statusChipsData = data));
    }

    private fetchCustomColumns() {
        const workflowId = this._workflowAndRepo?.workflowId;
        if (!workflowId) {
            this.customColumns = [];
            this.customColumnOptions = [];
            return;
        }
        this._subscriptions.unsubscribeSafe('custom-columns');
        this._subscriptions['custom-columns'] = this.workflowService
            .getCustomColumns(workflowId)
            .pipe(first())
            .subscribe({
                next: (columns) => {
                    this.customColumns = columns;
                    this.customColumnOptions =
                        customColumnsToColumnOptions(columns);

                    // BehaviorSubjects already emitted (via syncColumnConfig) before
                    // custom columns finished loading, so manually trigger a rebuild
                    // of the views that depend on them.
                    this.columnOptionsService.nestedColumnOptions$
                        .pipe(take(1))
                        .subscribe((opts) =>
                            this.rebuildNestedViewOptions(opts.hiddenColumns),
                        );

                    this.columnOptionsService.discColumnOptions$
                        .pipe(take(1))
                        .subscribe((opts) =>
                            this.rebuildDiscViewOptions(opts.hiddenColumns),
                        );
                },
            });
    }

    private getInboxChoices() {
        this.inboxChoicesLoading = true;

        this._subscriptions['get-inbox-choices'] = this.userService
            .getInboxChoices()
            .subscribe({
                next: (choices) => {
                    if (choices) {
                        this.selectedWorkflow = choices.workflowId;
                        this.showMyItems = choices.showOnlyUserItems;
                        this.selectedTableView = choices.discrepancy.isActive
                            ? TableView.Discrepancy
                            : TableView.Document;
                        this.setSavedStates();
                        this.setWorkflowAndRepo();
                    }
                },
                error: () => {
                    this.inboxChoicesLoading = false;
                    this.getWorkflows();
                },
                complete: () => {
                    this.inboxChoicesLoading = false;
                    this.getWorkflows();
                },
            });
    }

    private setInboxChoices() {
        this._subscriptions['set-inbox-choices'] =
            this.userDataService.userInboxChoices$.subscribe({
                next: (choices) => {
                    if (choices) {
                        this.inboxChoices = choices;
                        this.setSavedStates();
                    }
                },
            });
    }

    private setSavedStates() {
        if (this.inboxChoices) {
            this.selectedStates =
                this.selectedTableView === TableView.Discrepancy
                    ? (this.inboxChoices.discrepancy.states ?? [])
                    : (this.inboxChoices.document.states ?? []);
        }
    }

    private reconcileSelectedWorkflow() {
        const availableWorkflowIds = new Set(
            this.workflowInstanceOptions.map(
                (option) => option.value as number,
            ),
        );
        const selectedWorkflow = this.selectedWorkflow;

        if (
            selectedWorkflow != null &&
            availableWorkflowIds.has(selectedWorkflow)
        ) {
            this.setWorkflowAndRepo();
            return;
        }

        if (this.workflowInstanceOptions.length === 1) {
            this.selectedWorkflow = this.workflowInstanceOptions[0]
                .value as number;
            this.setWorkflowAndRepo();
            return;
        }

        this.selectedWorkflow = undefined;
        this.workflowAndRepo = undefined;
        this.closePanel();
    }
}
